import axios from "axios";
const API_BASE_URL =
  process.env.REACT_APP_API_URL || "https://riskreport.almata.co.jp:3400/api";

class ApiClient {
  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000,
      headers: {
        "Content-Type": "application/json",
      },
    });

    this.setupInterceptors();
  }

  setupInterceptors() {
    // Request interceptor
    this.client.interceptors.request.use(
      (config) => {
        const token = sessionStorage.getItem("authToken");
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor
    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        
        // Don't try to refresh token for login requests or if no token exists
        const isLoginRequest = originalRequest.url?.includes('/auth/login');
        const hasToken = sessionStorage.getItem("authToken");
        
        if (error.response?.status === 401 && !originalRequest._retry && !isLoginRequest && hasToken) {
          originalRequest._retry = true;
          
          try {
            const newToken = await this.refreshToken();
            if (newToken) {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              return this.client(originalRequest);
            }
          } catch (refreshError) {
            this.handleAuthError(error);
            return Promise.reject(refreshError);
          }
        }
        
        // For login requests or when no token exists, handle auth error but don't try refresh
        if (isLoginRequest || !hasToken) {
          return Promise.reject(error);
        }
        
        this.handleAuthError(error);
        return Promise.reject(error);
      }
    );
  }

  handleAuthError(error) {
    const status = error.response?.status;
    const message = error.response?.data?.message;

    if (
      (status === 401 || status === 403) &&
      sessionStorage.getItem("authToken")
    ) {
      if (
        message?.includes("token") ||
        message?.includes("expired") ||
        message?.includes("Invalid")
      ) {
        this.clearAuthState();
        this.redirectToLogin();
      }
    }
  }

  async refreshToken() {
    const refreshToken = sessionStorage.getItem("refreshToken");
    if (!refreshToken) {
      throw new Error("No refresh token available");
    }
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/refresh-token`, {
        refreshToken: refreshToken
      });
      const { accessToken, refreshToken: newRefreshToken } = response.data;
      sessionStorage.setItem("authToken", accessToken);
      if (newRefreshToken) {
        sessionStorage.setItem("refreshToken", newRefreshToken);
      }
      return accessToken;
    } catch (error) {
      this.clearAuthState();
      throw error;
    }
  }

  clearAuthState() {
    sessionStorage.removeItem("authToken");
    sessionStorage.removeItem("refreshToken");
    sessionStorage.removeItem("user");
  }

  redirectToLogin() {
    if (window.__REDUX_STORE__) {
      window.__REDUX_STORE__.dispatch({ type: "TOKEN_EXPIRED_SAGA" });
    } else {
      window.location.href = "/login";
    }
  }

  // Standard HTTP methods
  get(url, config = {}) {
    return this.client.get(url, config);
  }

  post(url, data = {}, config = {}) {
    return this.client.post(url, data, config);
  }

  put(url, data = {}, config = {}) {
    return this.client.put(url, data, config);
  }
  patch(url, data = {}, config = {}) {
    return this.client.patch(url, data, config);
  }

  delete(url, config = {}) {
    return this.client.delete(url, config);
  }

  // Form data methods
  postFormData(url, formData, config = {}) {
    return this.client.post(url, formData, {
      ...config,
      headers: {
        ...config.headers,
        "Content-Type": "multipart/form-data",
      },
    });
  }

  putFormData(url, formData, config = {}) {
    return this.client.patch(url, formData, {
      ...config,
      headers: {
        ...config.headers,
        "Content-Type": "multipart/form-data",
      },
    });
  }
}

const apiClient = new ApiClient();

// Store reference for Redux integration
export const setStore = (storeInstance) => {
  window.__REDUX_STORE__ = storeInstance;
};

export { apiClient as default };
